'use server';

import sharp from 'sharp';

import { ConsoleLogger } from '@/src/utils/logger';

const logger = new ConsoleLogger('rapid-api');

const RAPIDAPI_KEY = process.env.X_RAPIDAPI_KEY || '';
const RAPIDAPI_HOST = process.env.X_RAPIDAPI_HOST || '';
const INSTAGRAM_API_BASE_URL = 'https://social-api4.p.rapidapi.com/';

/**
 * Преобразует URL файла в строку формата base64
 *
 * @description
 * Функция загружает файл по указанному URL и конвертирует его в формат base64.
 * Поддерживает как изображения, так и видео файлы.
 * Для изображений выполняет дополнительную валидацию с помощью sharp.
 *
 * @param {string} url - URL файла для загрузки и конвертации
 *
 * @returns {Promise<string>} Строка в формате data URL (data:mimetype;base64,...)
 *
 * @throws {Error} "Network response was not ok" - если ответ сервера не 200 OK
 * @throws {Error} "Invalid image dimensions" - если не удалось получить размеры изображения
 * @throws {Error} "Invalid image format" - если формат изображения некорректный
 * @throws {Error} "Unsupported content type" - если тип контента не поддерживается
 * @throws {Error} "Failed to fetch file" - при общих ошибках загрузки
 *
 * @example
 * ```typescript
 * // Для изображения
 * const imageBase64 = await fetchUrlToBase64('https://example.com/image.jpg');
 * // Результат: data:image/jpeg;base64,/9j/4AAQSkZJRg...
 *
 * // Для видео
 * const videoBase64 = await fetchUrlToBase64('https://example.com/video.mp4');
 * // Результат: data:video/mp4;base64,AAAAIGZ0eXBtcDQy...
 * ```
 */
export async function fetchUrlToBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType =
      response.headers.get('content-type') || 'application/octet-stream';

    // Проверяем тип контента
    if (contentType.startsWith('video/')) {
      // Для видео просто конвертируем в base64
      const base64 = Buffer.from(arrayBuffer).toString('base64');
      return `data:${contentType};base64,${base64}`;
    }

    if (contentType.startsWith('image/')) {
      // Для изображений проверяем метаданные через sharp
      try {
        const metadata = await sharp(Buffer.from(arrayBuffer)).metadata();
        if (!metadata.width || !metadata.height) {
          throw new Error('Invalid image dimensions');
        }
      } catch (sharpError) {
        logger.error('Sharp validation failed:', sharpError);
        throw new Error('Invalid image format');
      }

      const base64 = Buffer.from(arrayBuffer).toString('base64');
      return `data:${contentType};base64,${base64}`;
    }

    throw new Error(`Unsupported content type: ${contentType}`);
  } catch (error) {
    logger.error(`Failed to fetch file from ${url}:`, error);
    throw new Error('Failed to fetch file.');
  }
}

async function fetchInstagramData(
  endpoint: string,
  params: Record<string, string | undefined>,
): Promise<any> {
  try {
    const url = new URL(`${INSTAGRAM_API_BASE_URL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, value);
      }
    });

    const headers = new Headers({
      'X-RapidAPI-Key': RAPIDAPI_KEY,
      'X-RapidAPI-Host': RAPIDAPI_HOST,
    });

    const response = await fetch(url.toString(), { headers });

    if (!response.ok) {
      throw new Error('Failed to fetch Instagram data.');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    logger.error(
      `Failed to fetch Instagram data from endpoint: ${endpoint}`,
      error,
    );
    throw new Error('Failed to fetch Instagram data.');
  }
}

export const getFileUrl = async (url: string) => {
  try {
    if (url.includes('instagram.com')) {
      const isPostUrl = url.includes('/p/');
      const endpoint = isPostUrl ? 'v1/post_info' : 'v1.2/posts';
      const params: Record<string, string | undefined> = isPostUrl
        ? {
            code_or_id_or_url: url,
            url_embed_safe: 'false',
            include_insights: 'false',
          }
        : { username_or_id_or_url: url };

      const data = await fetchInstagramData(endpoint, params);
      const items = isPostUrl
        ? [data.thumbnail_url]
        : data.items
            .slice(0, 9)
            .map((item: { thumbnail_url: string }) => item.thumbnail_url);

      const base64Strings = await Promise.all(items.map(fetchUrlToBase64));
      return isPostUrl ? { file: base64Strings[0] } : { files: base64Strings };
    }
    return { file: await fetchUrlToBase64(url) };
  } catch (error) {
    logger.error(`Error in getImageUrl function for URL: ${url}`, error);
    return { error: 'Image not found!' };
  }
};
